import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ACTIVITY_OVERVIEW_STATUS_FILTERS, type ActivityOverviewStatusFilter } from '@sparkapp/shared-types';
import { Card, CardContent } from '@/components/ui/card';
import { useActivityOverview } from '../hooks/useActivityOverview';
import { StatusStatCards } from '../components/activity-overview/StatusStatCards';
import { StatusFilter } from '../components/activity-overview/StatusFilter';
import { DomainTiles } from '../components/activity-overview/DomainTiles';
import { AllDomainsView } from '../components/activity-overview/AllDomainsView';
import { DomainDetail } from '../components/activity-overview/DomainDetail';

const DEFAULT_STATUS: ActivityOverviewStatusFilter = 'published';

function parseStatus(raw: string | null): ActivityOverviewStatusFilter {
  return (ACTIVITY_OVERVIEW_STATUS_FILTERS as readonly string[]).includes(raw ?? '')
    ? (raw as ActivityOverviewStatusFilter)
    : DEFAULT_STATUS;
}

/**
 * Activity Overview — read-only catalogue picture: status counts, domain tiles,
 * then either the all-domains view (health, Preference × Domain heatmap, packs,
 * play types) or one domain's detail. Status and domain live in the URL
 * (`?status=draft&domain=communication`).
 */
export default function ActivityOverviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = parseStatus(searchParams.get('status'));
  const domainKey = searchParams.get('domain');
  const { data, isLoading, error } = useActivityOverview(status);
  // An unknown key falls back to the all-domains view.
  const selected = data?.domains.find((d) => d.key === domainKey) ?? null;

  /** Set or clear one URL param, keeping the others. `null` (or the default) removes it. */
  const setParam = useCallback(
    (key: 'status' | 'domain', value: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === null || (key === 'status' && value === DEFAULT_STATUS)) next.delete(key);
        else next.set(key, value);
        return next;
      });
    },
    [setSearchParams]
  );
  const setStatus = (next: ActivityOverviewStatusFilter) => setParam('status', next);
  const setDomain = useCallback((key: string | null) => setParam('domain', key), [setParam]);

  useEffect(() => {
    if (!domainKey) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDomain(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [domainKey, setDomain]);

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-pine-display font-display text-pine-ink mb-2">Overview</h1>
        <p className="text-body text-pine-ink-soft">
          Distribution of the activity catalogue across domains, skills, preferences, packs and play
          types — and where the gaps are.
        </p>
      </motion.div>

      {/* Header stat cards — always unfiltered */}
      <StatusStatCards counts={data?.statusCounts} isLoading={isLoading} />

      <StatusFilter value={status} onChange={setStatus} />

      {error ? (
        <Card className="bg-pine-card border-pine-danger shadow-pine-card rounded-pine-card">
          <CardContent className="pt-6">
            <p className="text-pine-danger font-semibold">Failed to load the activity overview.</p>
            <p className="text-caption text-pine-ink-soft mt-1">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </CardContent>
        </Card>
      ) : isLoading || !data ? (
        <Card className="bg-pine-card border-pine-line shadow-pine-card rounded-pine-card">
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="h-10 bg-pine-line animate-pulse rounded" />
              <div className="h-10 bg-pine-line animate-pulse rounded" />
              <div className="h-10 bg-pine-line animate-pulse rounded" />
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="space-y-2">
            <DomainTiles
              domains={data.domains}
              totalActivities={data.totalActivities}
              selectedKey={selected?.key ?? null}
              onSelect={setDomain}
            />
            {data.noActiveDomainCount > 0 && (
              <p className="text-caption text-amber-800">
                {data.noActiveDomainCount} activities have no active primary domain and are not
                shown in any domain.
              </p>
            )}
          </div>
          <motion.div
            key={selected?.key ?? 'all'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
          >
            {selected ? (
              <DomainDetail
                domain={selected}
                totalActivities={data.totalActivities}
                status={status}
                preferences={data.preferences}
                packs={data.packs}
                playTypes={data.playTypes}
              />
            ) : (
              <AllDomainsView data={data} status={status} onSelectDomain={setDomain} />
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}
